import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function TerminosPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>

        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Términos y Condiciones de Uso</h1>
          <p className="text-slate-500 mb-8">Última actualización: Marzo 2025</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Aceptación de los Términos</h2>
              <p className="text-slate-600 mb-4">
                Al acceder y utilizar la plataforma KINOSIS (en adelante, "el Servicio"), usted acepta estar sujeto a estos Términos y Condiciones de Uso. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder al Servicio.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Descripción del Servicio</h2>
              <p className="text-slate-600 mb-4">
                KINOSIS es una plataforma de gestión deportiva que permite a clubes, academias y ligas administrar sus integrantes, categorías, pagos y finanzas. El Servicio se ofrece en modalidades gratuita (Free) y de pago (Pro).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Cuentas de Usuario</h2>
              <p className="text-slate-600 mb-4">
                <strong>3.1 Registro:</strong> Para utilizar el Servicio, debe crear una cuenta proporcionando información veraz y actualizada. Usted es responsable de mantener la confidencialidad de su cuenta y contraseña.
              </p>
              <p className="text-slate-600 mb-4">
                <strong>3.2 Cuentas Gratuitas:</strong> Las cuentas gratuitas están sujetas a limitaciones de funcionalidad (1 categoría, máximo 20 integrantes). KINOSIS se reserva el derecho de suspender o eliminar cuentas gratuitas por:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Inactividad prolongada (más de 6 meses sin uso)</li>
                <li>Uso indebido o abusivo del Servicio</li>
                <li>Violación de estos Términos y Condiciones</li>
                <li>Actividades fraudulentas o ilegales</li>
              </ul>
              <p className="text-slate-600 mb-4">
                <strong>3.3 Cuentas Premium (Pro):</strong> Las cuentas de pago ofrecen funcionalidades extendidas según el plan contratado. El incumplimiento de pago puede resultar en la suspensión temporal de las funciones premium.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Uso Aceptable</h2>
              <p className="text-slate-600 mb-4">
                El usuario se compromete a utilizar el Servicio de manera responsable y legal. Está prohibido:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Utilizar el Servicio para fines ilegales o no autorizados</li>
                <li>Intentar acceder a áreas restringidas del sistema</li>
                <li>Transmitir virus, malware o código malicioso</li>
                <li>Recopilar información de otros usuarios sin su consentimiento</li>
                <li>Suplantar la identidad de otra persona u organización</li>
                <li>Utilizar el Servicio para enviar spam o comunicaciones no solicitadas</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Propiedad Intelectual</h2>
              <p className="text-slate-600 mb-4">
                El Servicio, incluyendo su diseño, código, logos, marcas y contenido, es propiedad de KINOSIS y está protegido por las leyes de propiedad intelectual. Se prohíbe la reproducción, distribución o modificación sin autorización expresa.
              </p>
              <p className="text-slate-600 mb-4">
                Los datos ingresados por los usuarios (información de integrantes, pagos, etc.) permanecen como propiedad del usuario, quien otorga a KINOSIS una licencia limitada para procesar dicha información con el único fin de proveer el Servicio.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Limitación de Responsabilidad</h2>
              <p className="text-slate-600 mb-4">
                KINOSIS proporciona el Servicio "tal cual" y "según disponibilidad". No garantizamos que el Servicio sea ininterrumpido, seguro o libre de errores. En la máxima medida permitida por la ley:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>No seremos responsables por daños indirectos, incidentales o consecuentes</li>
                <li>Nuestra responsabilidad total no excederá el monto pagado por el usuario en los últimos 12 meses</li>
                <li>No somos responsables por pérdida de datos debido a fallas técnicas o errores del usuario</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Pagos y Reembolsos</h2>
              <p className="text-slate-600 mb-4">
                <strong>7.1</strong> Los planes de pago se facturan de forma mensual o anual según la modalidad elegida.
              </p>
              <p className="text-slate-600 mb-4">
                <strong>7.2</strong> Los pagos no son reembolsables, excepto en casos excepcionales evaluados individualmente.
              </p>
              <p className="text-slate-600 mb-4">
                <strong>7.3</strong> KINOSIS se reserva el derecho de modificar los precios con previo aviso de 30 días.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Modificaciones del Servicio</h2>
              <p className="text-slate-600 mb-4">
                KINOSIS puede modificar, suspender o discontinuar cualquier aspecto del Servicio en cualquier momento, con o sin previo aviso. No seremos responsables ante usted o terceros por cualquier modificación, suspensión o discontinuación del Servicio.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Terminación</h2>
              <p className="text-slate-600 mb-4">
                Podemos terminar o suspender su cuenta inmediatamente, sin previo aviso, por cualquier motivo, incluyendo el incumplimiento de estos Términos. Tras la terminación, su derecho a usar el Servicio cesará inmediatamente.
              </p>
              <p className="text-slate-600 mb-4">
                Usted puede cancelar su cuenta en cualquier momento contactándonos. Los datos podrán ser eliminados después de 30 días de la cancelación.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Legislación Aplicable</h2>
              <p className="text-slate-600 mb-4">
                Estos términos se regirán e interpretarán de acuerdo con las leyes de la República de Chile, sin considerar sus disposiciones sobre conflictos de leyes. Cualquier disputa será sometida a la jurisdicción de los tribunales competentes de Santiago, Chile.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Cambios en los Términos</h2>
              <p className="text-slate-600 mb-4">
                Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en el Servicio. El uso continuado del Servicio después de cualquier cambio constituye la aceptación de los nuevos términos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Contacto</h2>
              <p className="text-slate-600 mb-4">
                Si tiene preguntas sobre estos Términos y Condiciones, puede contactarnos a través de:
              </p>
              <p className="text-slate-600">
                <strong>Email:</strong> soporte@kinosis.cl
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
